import { Component, Input, OnDestroy, OnInit, Output, EventEmitter, ViewChildren, ElementRef, QueryList } from '@angular/core';
import { CaseRequirement, Note, NoteResponse, PatchNoteRequest, UWBaseProps, UWNoteListFilter } from '@Underwriting/models';
import { UWWorkbenchService } from '@Underwriting/services';
import UWNotesFeatureToggleDataService from 'app/case-summary/underwriting/services/uw-notes-feature-toggle/uw-notes-feature-toggle.data.service';
import { SubSink } from 'subsink';
import { AppService } from '@App';
import { IdleService } from '@Services/utility/idle.service';
import { CaseNoteService } from '@Services/case/case-note.service';
import moment from 'moment';
import { AuthLegacyService } from '@App/auth/auth-legacy.service';
import { NgIf, NgFor } from '@angular/common';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { FormsModule } from '@angular/forms';
import { RxReactiveFormsModule } from '@rxweb/reactive-form-validators';
import { NoteTypes } from '@Enums';
import { ReviewedObjectRequest } from '@Models/case/reviewed-object.request';
import { UserSettingsService } from '@Services/global/user-settings.service';
import { RichTextEditorComponent } from '@Shared/components/rich-text-editor/rich-text-editor.component';
import { MatChipListbox, MatChipOption } from '@angular/material/chips';
import { CaseWorkbenchTaskNoteResponse, UWTaskNotesDataService } from '@Services';

@Component({
    selector: 'uw-notes-list',
    templateUrl: './uw-notes-list.component.html',
    styleUrls: ['./uw-notes-list.component.scss'],
    imports: [NgIf, NgFor, MatChipListbox, MatChipOption, MatIconButton, MatTooltip, MatIcon, MatFormField, MatInput, CdkTextareaAutosize, FormsModule, RxReactiveFormsModule, MatButton, RichTextEditorComponent]
})
export default class UWNotesList implements OnInit, OnDestroy {
    @Input() baseProps: UWBaseProps;

    private _notes: Note[] = [];
    @Input()
    set notes(value: Note[]) {
        this._notes = value || [];
        if (this.updatedRequirementTaskNoteStyling) {
            setTimeout(() => this._notes.forEach(note => this.updateExpandLinkVisibility(note.id)));
        }
    }
    get notes(): Note[] { return this._notes; }

    @Input() showRequirementHandle: boolean;
    @Input() caseRequirements: CaseRequirement[] = [];
    @Input() caseTaskNotes: CaseWorkbenchTaskNoteResponse[] = [];
    @Input() caseTaskNotesLoaded = false;
    @ViewChildren('editNoteTextArea') noteInputs!: QueryList<ElementRef<HTMLTextAreaElement>>;


    @Output() editingStateChanged = new EventEmitter<boolean>();

    isHiddenNoteFeatureEnabled : boolean | undefined = false;
    isHiddenNoteAccountSettingEnabled : boolean | undefined = false;
    isEditableNoteFeatureEnabled : boolean | undefined = false;
    isEditableNoteAccountSettingEnabled : boolean | undefined = false;
    editableNoteDaysBackAccountSetting: number | undefined ;
    updatedRequirementsTableDesign : boolean | undefined = false;
    updatedRequirementTaskNoteStyling: boolean | undefined = false;

    allSelected: boolean = true;
    manualSelected: boolean = false;
    agentCommunicationSelected: boolean = false;
    requirementStatusSelected: boolean = false;
    evidenceUpdateSelected: boolean = false;
    documentSelected: boolean = false;

    subs = new SubSink();
    expandedNotes = new Set<string | number>();

    editingNoteId: string | null = null;
    editedMessage: string = '';
    currentUserId: string = '';


    constructor(
        private underWriterWorkbenchService: UWWorkbenchService,
        private uwNotesFeatureToggle: UWNotesFeatureToggleDataService,
        private appService: AppService,
        private idleService: IdleService,
        private authService: AuthLegacyService,
        private caseNoteService: CaseNoteService,
        private userSettingsService: UserSettingsService,
        private taskNotesService: UWTaskNotesDataService
    ) { }

    //#region Helpers

    createPatchRequest(noteId: string, isInternalNote: boolean): PatchNoteRequest {
        return {
            "noteId": noteId,
            "isInternalNote": isInternalNote
        };
    }

    canEditNote(note: NoteResponse): boolean {
        const result = this.isEditableNoteFeatureEnabled
                    && this.isEditableNoteAccountSettingEnabled
                    && moment().utc().diff(moment.utc(note.audit.createdOn), 'days') < this.editableNoteDaysBackAccountSetting
                    && !this.baseProps.readonly
                    && note.audit.createdBy === this.currentUserId
                    && this.editingNoteId !== note.id
                    && this.showRequirementHandle;

        return result;
    }

    disableEditNote(note: NoteResponse): boolean {
        return this.editingNoteId !== null && this.editingNoteId !== note.id;
    }

    showUnreadIcon(note: Note): boolean {
        return this.editingNoteId !== note.id && !this.showRequirementHandle && note.noteTypeId === NoteTypes.AgentCommunicationApi;
    }

    isAgentCommunicationNote(note: Note): boolean {
        return this.editingNoteId !== note.id && !this.showRequirementHandle && (note.noteTypeId === NoteTypes.AgentCommunicationApi || note.noteTypeId === NoteTypes.AgentCommunicationUI);
    }

    toggleNoteFilter(filterType: string, filterValue: any) {        
        if (filterValue.isUserInput === false) {
            return;
        }

        switch (filterType) {
            case 'all': 
                this.allSelected = true;
                this.manualSelected = false;
                this.agentCommunicationSelected = false;
                this.requirementStatusSelected = false;
                this.evidenceUpdateSelected = false;
                this.documentSelected = false;
                break;
            case 'manual':
                this.manualSelected = filterValue.selected;
                this.allSelected = false;
                break;
            case 'agentCommunication':
                this.agentCommunicationSelected = filterValue.selected;
                this.allSelected = false;
                break;
            case 'requirementStatus':
                this.requirementStatusSelected = filterValue.selected;
                this.allSelected = false;   
                break;
            case 'evidenceUpdate':
                this.evidenceUpdateSelected = filterValue.selected;
                this.allSelected = false;
                break;
            case 'document':
                this.documentSelected = filterValue.selected;
                this.allSelected = false;
                break;
        }

        const noteFilterSettings: UWNoteListFilter = {
            allSelected: this.allSelected,
            manualSelected: this.manualSelected,
            agentCommunicationSelected: this.agentCommunicationSelected,
            requirementStatusSelected: this.requirementStatusSelected,
            evidenceUpdateSelected: this.evidenceUpdateSelected,
            documentSelected: this.documentSelected
        };

        this.userSettingsService.saveSetting(UserSettingsService.UWREQUIREMENT_NOTES_FILTER_KEY, JSON.stringify(noteFilterSettings)).subscribe();
    }

    toggleNoteVisibility(noteId: string) {

        const note = this.notes.filter(x => x.id === noteId);
        if (note.length !== 1)
            return;

        if (note[0].isInternalNote)
            note[0].isInternalNote = false;
        else
            note[0].isInternalNote = true;

        const request = this.createPatchRequest(note[0].id, note[0].isInternalNote);

        this.subs.add(this.underWriterWorkbenchService.patchNote(this.baseProps.account.id, request).subscribe());
    }

    setEditingState(isEditing: boolean) {
        this.editingStateChanged.emit(isEditing);
    }

    editNote(note: Note) {
        this.editingNoteId = note.id;
        this.editedMessage = note.message;
        this.idleService.areUwwNotesInEditMode = true;
        this.setEditingState(true);
    }

    saveNote(note: Note, isAutoSave: boolean = false) {
        if (note.caseTaskNote?.taskId) {
            this.saveTaskNote(note, isAutoSave);
            return;
        }

        const patchNoteRequest: PatchNoteRequest = { noteId: note.id, message: this.editedMessage };
        this.subs.add(this.underWriterWorkbenchService.patchNote(this.baseProps.account.id, patchNoteRequest).subscribe(() => {
            note.message = this.editedMessage;
            this.editingNoteId = null;
            this.editedMessage = '';
            this.idleService.areUwwNotesInEditMode = false;
            this.setEditingState(false);

            if (isAutoSave) {
                this.authService.logout();
            }
        }));
    }

    saveTaskNote(note: Note, isAutoSave: boolean = false){
        this.subs.add(
            this.taskNotesService.updateTaskNote(this.baseProps?.case?.id, note.caseTaskNote?.taskId, { message: this.editedMessage, isInternalNote: false, id: this.editingNoteId }).subscribe({
                next: (notes) => {
                    this.editingNoteId = null;
                    this.idleService.areUwwNotesInEditMode = false;
                    this.notes = notes;

                    this.setEditingState(false);
                    if (isAutoSave) {
                        this.authService.logout();
                    }
                }
            })
        );
    }
    autoSaveNote() {
        if (this.editingNoteId) {
            const note = this.notes.find(n => n.id === this.editingNoteId);
            if (note) {
                this.saveNote(note, true);
            }
            else {
                this.authService.logout();
            }
        }
        else {
            this.authService.logout();
        }
    }

    markNoteAsReviewed(note: NoteResponse) {
        if (note.isReviewed) {
            return;
        }
        const caseRequirement = this.caseRequirements?.find(
            cr => cr.id === note.caseRequirementNote?.caseRequirementId
        );
        const markObjectAsReviewedRequest: ReviewedObjectRequest = { objectId: note.id, caseId: this.baseProps.case.id, caseRequirement };
        this.subs.add(this.caseNoteService.markObjectAsReviewed(markObjectAsReviewedRequest, this.baseProps.client.id, this.baseProps.case.id).subscribe({
            next: () => {
                note.isReviewed = true;
            }
        }));
    }

    discardEdit() {
        this.editingNoteId = null;
        this.editedMessage = '';
        this.setEditingState(false);
    }

    toggleNoteExpanded(noteId: string | number): void {
        if (this.expandedNotes.has(noteId)) {
            this.expandedNotes.delete(noteId);
        } else {
            this.expandedNotes.add(noteId);
        }
    }

    isNoteExpanded(noteId: string | number): boolean {
        return this.expandedNotes.has(noteId);
    }

    private updateExpandLinkVisibility(noteId: string | number): void {
        const messageElement = document.getElementById(`message-${noteId}`);
        const expandLink = document.getElementById(`expand-${noteId}`);
        if (!messageElement || !expandLink) return;

        // Measure clamped height (class applied), then full height (class removed).
        // Comparing the two avoids manually recalculating line-height thresholds.
        const clampedHeight = messageElement.offsetHeight;
        messageElement.classList.remove('message-condensed');
        const fullHeight = messageElement.scrollHeight;
        messageElement.classList.add('message-condensed');

        expandLink.style.display = fullHeight > clampedHeight ? 'block' : 'none';
    }

    //#endregion
    //#region Lifecycle

    ngOnInit() {

        this.subs.add(this.uwNotesFeatureToggle.loadHiddenNotesFeatureSetting(this.baseProps.account).subscribe((noteSettings) => {
            this.isHiddenNoteFeatureEnabled = noteSettings.featureToggle;
            this.isHiddenNoteAccountSettingEnabled = noteSettings.accountSetting;
            this.isEditableNoteFeatureEnabled = noteSettings.editableNoteFeatureToggle;
            this.isEditableNoteAccountSettingEnabled = noteSettings.editableNoteAccountSetting;
            this.editableNoteDaysBackAccountSetting = noteSettings.editableNoteDaysBackAccountSetting;
            this.updatedRequirementsTableDesign = noteSettings.updatedRequirementsTableDesign;    
            this.updatedRequirementTaskNoteStyling = noteSettings.updatedRequirementTaskNoteStyling;
            if (this.updatedRequirementTaskNoteStyling) {
                // Notes may already be loaded — schedule after paint so DOM is ready
                setTimeout(() => this._notes.forEach(note => this.updateExpandLinkVisibility(note.id)));
            }
        }));

        this.subs.add(this.userSettingsService.getSetting<string | undefined>(UserSettingsService.UWREQUIREMENT_NOTES_FILTER_KEY).subscribe((response) => {
            if (!response) {
                return;
            }
            
            const noteListFilter = JSON.parse(response) as UWNoteListFilter;
            this.allSelected = noteListFilter.allSelected ?? true;
            this.manualSelected = noteListFilter.manualSelected ?? true;
            this.agentCommunicationSelected = noteListFilter.agentCommunicationSelected ?? true;
            this.requirementStatusSelected = noteListFilter.requirementStatusSelected ?? true;
            this.evidenceUpdateSelected = noteListFilter.evidenceUpdateSelected ?? true;
            this.documentSelected = noteListFilter.documentSelected ?? true;
        }));

        this.currentUserId = this.appService.getUserId();

        const idleSubscription = this.idleService.triggeredSignoutAutoSaveUwwEditNotes.subscribe(async _data => {
            await this.autoSaveNote();
        });

        this.subs.add(idleSubscription);
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
        this.idleService.areUwwNotesInEditMode = false;
    }

    //#endregion
}
